import React, { useState } from 'react';
import { Fuel, Wrench, Droplet, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'fuel', label: 'Fuel', Icon: Fuel },
  { id: 'maintenance', label: 'Maintenance', Icon: Wrench },
  { id: 'wash', label: 'Car wash', Icon: Droplet },
  { id: 'other', label: 'Other', Icon: Receipt },
];

const LogExpenseModal = ({ driverId, onClose, onSaved }) => {
  const [category, setCategory] = useState('fuel');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      alert('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/driver/v2/${driverId}/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: n, category, note: note || null }),
      });
      if (!r.ok) throw new Error('save failed');
      onSaved?.();
    } catch {
      alert('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="log-expense-modal"
      >
        <h3 className="text-lg font-semibold text-gray-900">Log expense</h3>
        <p className="text-xs text-gray-500 mt-0.5 mb-4">
          Tracking expenses gives you accurate net-earnings on every period.
        </p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {CATEGORIES.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setCategory(id)}
              data-testid={`log-expense-cat-${id}`}
              className={`h-20 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-colors ${
                category === id ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-emerald-200'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-semibold">{label}</span>
            </button>
          ))}
        </div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Amount (MWK)</label>
        <input
          type="number"
          min={0}
          step={100}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 4000"
          data-testid="log-expense-amount"
          className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-base tabular-nums"
        />
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1 mt-3">Note (optional)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Total Fuel · Old Town"
          data-testid="log-expense-note"
          maxLength={80}
          className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
        />
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 h-12 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="log-expense-save"
            className="flex-1 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold text-sm"
          >
            {saving ? 'Saving…' : 'Save expense'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogExpenseModal;
